import { Eraser, Loader2, Save } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';

import { Button } from '@/shared/ui/button';

/** Nome do arquivo enviado. Só aparece em log e na coluna `nomeOriginal`. */
const NOME_DO_ARQUIVO = 'rubrica.png';

/**
 * Rubrica desenhada com o dedo ou o mouse.
 *
 * ⚠️ O traço vira **PNG e sobe como arquivo** — o tipo `DESENHADA` guarda
 * imagem, não data URL no banco. A V1 gravava o `toDataURL()` cru numa coluna
 * de texto, e por isso o PDF tinha que ser montado no browser para reidratar.
 */
export function AssinaturaDesenhada({
  enviando,
  aoSalvar,
}: {
  enviando: boolean;
  aoSalvar: (arquivo: File) => void;
}) {
  const canvas = useRef<SignatureCanvas>(null);
  const moldura = useRef<HTMLDivElement>(null);
  const [largura, setLargura] = useState(0);
  const [vazio, setVazio] = useState(true);

  // O canvas precisa de largura em PIXELS, não em CSS: dimensionado por
  // porcentagem, o traço sai deslocado do cursor. Daí a medição do contêiner.
  useEffect(() => {
    const medir = () => {
      if (moldura.current) setLargura(Math.floor(moldura.current.getBoundingClientRect().width));
    };

    medir();
    window.addEventListener('resize', medir);
    return () => window.removeEventListener('resize', medir);
  }, []);

  const limpar = useCallback(() => {
    canvas.current?.clear();
    setVazio(true);
  }, []);

  const salvar = useCallback(() => {
    const elemento = canvas.current?.getCanvas();
    if (!elemento) return;

    elemento.toBlob((blob) => {
      if (blob) aoSalvar(new File([blob], NOME_DO_ARQUIVO, { type: 'image/png' }));
    }, 'image/png');
  }, [aoSalvar]);

  return (
    <div className="space-y-3">
      <div
        ref={moldura}
        className="relative border border-border rounded-lg bg-white overflow-hidden"
      >
        {/* Linha-guia: sem ela a rubrica sai flutuando no meio do quadro.
            `papel-suave`, e não `muted-foreground`: o quadro é branco fixo, e no
            tema escuro o cinza do tema é CLARO — a guia sumia no branco. */}
        <div className="absolute bottom-[42px] left-6 right-6 border-b border-dashed border-papel-suave/50 pointer-events-none" />

        {largura > 0 && (
          <SignatureCanvas
            ref={canvas}
            penColor="#1a1a2e"
            minWidth={1.5}
            maxWidth={3}
            onEnd={() => setVazio(false)}
            canvasProps={{
              width: largura,
              height: 200,
              className: 'cursor-crosshair touch-none',
              'aria-label': 'Área de desenho da rubrica',
            }}
          />
        )}
      </div>

      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          Desenhe a rubrica acima. Ela é enviada como imagem.
        </p>

        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" className="gap-2" onClick={limpar}>
            <Eraser className="w-4 h-4" />
            Limpar
          </Button>
          <Button
            type="button"
            size="sm"
            className="gap-2"
            disabled={vazio || enviando}
            onClick={salvar}
          >
            {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salvar rubrica
          </Button>
        </div>
      </div>
    </div>
  );
}
